const ACTIVE_ORDER_STATUSES = new Set(["pending", "preparing"]);

const normalizeNotes = (notes) => (notes == null ? "" : String(notes));
const normalizeAreaId = (value) => {
  if (value == null || value === "") {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

// Alert routing must follow the snapped production owner, not the live menu config.
const resolveProductionAreaId = (item) => {
  const candidates = [
    item?.productionAreaId,
    item?.production_area_id,
  ];

  for (const candidate of candidates) {
    const normalized = normalizeAreaId(candidate);
    if (normalized != null) {
      return normalized;
    }
  }

  return null;
};

const buildOrderLineKey = (item) => {
  if (item?.orderItemId != null) {
    return `orderItem:${item.orderItemId}`;
  }

  return `item:${item?.itemId ?? item?.id ?? "unknown"}:notes:${normalizeNotes(item?.notes)}`;
};

export const buildRequestSnapshot = (requests = []) => {
  const activeRequestIds = requests
    .filter((request) => request && request.status !== "completed" && request.id != null)
    .map((request) => Number(request.id))
    .sort((left, right) => left - right);

  return {
    activeRequestIds,
  };
};

export const buildOrderSnapshot = (orders = [], options = {}) => {
  const areaId = normalizeAreaId(options.areaId);

  return orders.reduce((snapshot, order) => {
    if (!order || order.id == null) {
      return snapshot;
    }

    if (!ACTIVE_ORDER_STATUSES.has(order.status) || order.payment_status === "paid") {
      return snapshot;
    }

    const lines = {};
    let totalQuantity = 0;

    (order.items || []).forEach((item) => {
      const quantity = Number(item?.quantity || 0);
      if (quantity <= 0) {
        return;
      }
      const itemArea = resolveProductionAreaId(item);
      if (areaId != null && itemArea !== null && itemArea !== areaId) {
        return;
      }

      const key = buildOrderLineKey(item);
      lines[key] = (lines[key] || 0) + quantity;
      totalQuantity += quantity;
    });

    if (totalQuantity <= 0) {
      return snapshot;
    }

    snapshot[String(order.id)] = {
      totalQuantity,
      lines,
    };

    return snapshot;
  }, {});
};

export const buildVenueAlertSnapshot = ({
  requests = [],
  pendingOrders = [],
  preparingOrders = [],
  completedOrders = [],
  areaId = null,
} = {}) => ({
  requests: buildRequestSnapshot(requests),
  orders: buildOrderSnapshot([
    ...(pendingOrders || []),
    ...(preparingOrders || []),
    ...(completedOrders || []),
  ], { areaId }),
});

export const diffVenueAlertSnapshots = (previousSnapshot, nextSnapshot) => {
  const previousRequestIds = new Set(previousSnapshot?.requests?.activeRequestIds || []);
  const nextRequestIds = nextSnapshot?.requests?.activeRequestIds || [];
  const nextOrders = nextSnapshot?.orders || {};
  const previousOrders = previousSnapshot?.orders || {};

  const hasRequestAlert = nextRequestIds.some((requestId) => !previousRequestIds.has(requestId));

  let hasOrderAlert = false;

  Object.entries(nextOrders).some(([orderId, nextOrder]) => {
    const previousOrder = previousOrders[orderId];

    if (!previousOrder) {
      hasOrderAlert = nextOrder.totalQuantity > 0;
      return hasOrderAlert;
    }

    if (nextOrder.totalQuantity > previousOrder.totalQuantity) {
      hasOrderAlert = true;
      return true;
    }

    const nextLines = nextOrder.lines || {};
    const previousLines = previousOrder.lines || {};

    hasOrderAlert = Object.entries(nextLines).some(([lineKey, nextQuantity]) => {
      const previousQuantity = previousLines[lineKey] || 0;
      return nextQuantity > previousQuantity;
    });

    return hasOrderAlert;
  });

  return {
    hasRequestAlert,
    hasOrderAlert,
  };
};
